import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Lock, Filter } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { articlesAPI, categoriesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { convertGoogleDriveUrl } from '../lib/imageUtils';

const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1638738105885-3e43f3b156a3?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesRes, categoriesRes] = await Promise.all([
          articlesAPI.getAll({
            search: searchParams.get('search') || undefined,
            category: searchParams.get('category') !== 'all' ? searchParams.get('category') : undefined
          }),
          categoriesAPI.getAll()
        ]);
        setArticles(articlesRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory && selectedCategory !== 'all') params.set('category', selectedCategory);
    setSearchParams(params);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set('category', value);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  return (
    <div className="paper-texture min-h-screen" data-testid="articles-page">
      {/* Header Section */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-accent font-medium text-sm uppercase tracking-widest">Heritage Archive</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary-foreground mt-4 mb-6">
              Articles & Stories
            </h1>
            <p className="text-lg text-primary-foreground/80">
              Explore documented narratives of Jabalpur's history, culture, architecture, and people. 
              {!isAuthenticated && (
                <span className="block mt-2 text-accent">
                  Free access to full articles with a reader account.
                </span>
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-[180px]" data-testid="category-select">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" data-testid="search-btn">Search</Button>
            </div>
          </form>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-muted h-48 mb-4"></div>
                  <div className="bg-muted h-6 w-3/4 mb-2"></div>
                  <div className="bg-muted h-4 w-full"></div>
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, idx) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  className="article-card group"
                >
                  <Link to={`/articles/${article.id}`} data-testid={`article-${article.id}`}>
                    <div className="img-zoom mb-4 border border-border">
                      <img 
                        src={convertGoogleDriveUrl(article.featured_image) || PLACEHOLDER_IMAGE}
                        alt={article.title}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="tag-badge">{article.category}</span>
                      {article.visibility === 'restricted' && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Lock className="h-3 w-3" /> Free with login
                        </span>
                      )}
                    </div>
                    <h2 className="font-heading text-xl font-semibold text-foreground mb-2 group-hover:text-accent transition-colors duration-300">
                      {article.title}
                    </h2>
                    <p className="text-muted-foreground text-sm line-clamp-3 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>By {article.author_name}</span>
                      <span>{new Date(article.created_at).toLocaleDateString()}</span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <h3 className="font-heading text-2xl text-foreground mb-4">No Articles Found</h3>
              <p className="text-muted-foreground mb-6">
                {searchParams.get('search') || searchParams.get('category') 
                  ? "Try adjusting your search or filter criteria."
                  : "Articles will appear here once published."}
              </p>
              {(searchParams.get('search') || searchParams.get('category')) && (
                <Button variant="outline" onClick={() => setSearchParams({})}>
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA for non-authenticated users */}
      {!isAuthenticated && articles.length > 0 && (
        <section className="py-12 bg-muted/30 border-t border-border">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <Lock className="h-8 w-8 mx-auto text-accent mb-4" />
            <h3 className="font-heading text-2xl font-semibold text-foreground mb-2">
              Free Access to Full Articles
            </h3>
            <p className="text-muted-foreground mb-4">
              Create a free account to read complete articles, view citations, and access the full research archive.
            </p>
            <p className="text-sm text-muted-foreground/70 mb-6 italic">
              A public heritage initiative — open access for all readers.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link to="/register">
                <Button data-testid="articles-register-btn">Create Free Account</Button>
              </Link>
              <Link to="/login">
                <Button variant="outline">Sign In</Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
